import { useState, useEffect } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Shield, AlertTriangle, Clock, Plus, FileText, Lock, CheckCircle2, User, Send, Settings, Mail, Copy, Link2, Trash2, ExternalLink, Archive, UserX, Database } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const SEV_COLORS = { low: 'text-[#A8E6CF] bg-[#A8E6CF]/10', medium: 'text-amber-400 bg-amber-500/10', high: 'text-red-400 bg-red-500/10', critical: 'text-red-500 bg-red-500/20' };

export default function IGCompliance() {
  const { user } = useAuth();
  const isAdmin = ['agency_admin', 'ceo'].includes(user?.role);
  const [tab, setTab] = useState('breaches');
  const [breaches, setBreaches] = useState([]);
  const [dsars, setDsars] = useState([]);
  const [breachDialog, setBreachDialog] = useState(false);
  const [dsarDialog, setDsarDialog] = useState(false);
  const [breachForm, setBreachForm] = useState({ description: '', severity: 'medium', subjects_affected: 0, data_categories: [], containment_actions: '' });
  const [dsarForm, setDsarForm] = useState({ requester_name: '', requester_email: '', subject_name: '', relationship: 'self', subject_type: 'client' });
  const [dpoSettings, setDpoSettings] = useState({ dpo_name: '', dpo_email: '', dpo_phone: '', ico_registration_number: '' });
  const [dpoEditing, setDpoEditing] = useState(false);
  const [inspectorTokens, setInspectorTokens] = useState([]);
  const [tokenDialog, setTokenDialog] = useState(false);
  const [tokenForm, setTokenForm] = useState({ inspector_name: '', purpose: 'CQC Inspection', hours: 72 });
  const [newToken, setNewToken] = useState(null);
  const [erasureRequests, setErasureRequests] = useState([]);
  const [erasureDialog, setErasureDialog] = useState(false);
  const [erasureForm, setErasureForm] = useState({ subject_id: '', reason: '' });
  const [clients, setClients] = useState([]);
  const [retentionPolicy, setRetentionPolicy] = useState({});
  const [retentionFlagged, setRetentionFlagged] = useState([]);

  useEffect(() => { fetchBreaches(); fetchDSARs(); if (isAdmin) { fetchDPO(); fetchTokens(); fetchErasure(); fetchRetention(); fetchClients(); } }, []);
  const fetchBreaches = async () => { try { const { data } = await axios.get(`${API}/api/ig-compliance/breaches`, { withCredentials: true }); setBreaches(data); } catch { /* ignore */ } };
  const fetchDSARs = async () => { try { const { data } = await axios.get(`${API}/api/ig-compliance/dsar`, { withCredentials: true }); setDsars(data); } catch { /* ignore */ } };
  const fetchDPO = async () => { try { const { data } = await axios.get(`${API}/api/org/dpo-settings`, { withCredentials: true }); setDpoSettings(data); } catch { /* ignore */ } };
  const fetchTokens = async () => { try { const { data } = await axios.get(`${API}/api/inspector/tokens`, { withCredentials: true }); setInspectorTokens(data); } catch { /* ignore */ } };
  const fetchErasure = async () => { try { const { data } = await axios.get(`${API}/api/ig-compliance/erasure-requests`, { withCredentials: true }); setErasureRequests(data); } catch { /* ignore */ } };
  const fetchRetention = async () => {
    try {
      const [pol, flagged] = await Promise.all([
        axios.get(`${API}/api/ig-compliance/retention-policy`, { withCredentials: true }),
        axios.get(`${API}/api/ig-compliance/retention-flagged`, { withCredentials: true }),
      ]);
      setRetentionPolicy(pol.data.retention_periods || {});
      setRetentionFlagged(flagged.data.flagged_records || []);
    } catch { /* ignore */ }
  };
  const fetchClients = async () => { try { const { data } = await axios.get(`${API}/api/clinical/my-clients`, { withCredentials: true }); setClients(data); } catch { /* ignore */ } };

  const saveDPO = async () => {
    try {
      await axios.put(`${API}/api/org/dpo-settings`, dpoSettings, { withCredentials: true });
      toast.success('DPO settings saved — breach alerts will go to this email');
      setDpoEditing(false);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save'); }
  };

  const generateToken = async () => {
    try {
      const { data } = await axios.post(`${API}/api/inspector/generate-token`, tokenForm, { withCredentials: true });
      setNewToken(data);
      fetchTokens();
      toast.success('Inspector token generated');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const revokeToken = async (id) => {
    try {
      await axios.put(`${API}/api/inspector/tokens/${id}/revoke`, {}, { withCredentials: true });
      toast.success('Token revoked'); fetchTokens();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const copyInspectorUrl = (token) => {
    const url = `${window.location.origin}/inspect/${token}`;
    navigator.clipboard.writeText(url);
    toast.success('Inspector URL copied to clipboard');
  };

  const saveBreach = async () => {
    try {
      const payload = { ...breachForm, containment_actions: breachForm.containment_actions ? breachForm.containment_actions.split('\n').filter(Boolean) : [] };
      await axios.post(`${API}/api/ig-compliance/breaches`, payload, { withCredentials: true });
      toast.success('Breach logged — 72hr ICO timer started'); setBreachDialog(false); fetchBreaches();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };
  const saveDSAR = async () => {
    try {
      await axios.post(`${API}/api/ig-compliance/dsar`, dsarForm, { withCredentials: true });
      toast.success('DSAR logged — 1 month deadline set'); setDsarDialog(false); fetchDSARs();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };
  const updateDSARStatus = async (id, status) => {
    try { await axios.put(`${API}/api/ig-compliance/dsar/${id}`, { status }, { withCredentials: true }); toast.success(`DSAR status → ${status}`); fetchDSARs(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };
  const updateBreachStatus = async (id, status) => {
    try { await axios.put(`${API}/api/ig-compliance/breaches/${id}`, { status }, { withCredentials: true }); toast.success(`Breach status → ${status}`); fetchBreaches(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="ig-compliance-page">
        <div>
          <h1 className="text-2xl font-heading font-bold text-foreground flex items-center gap-2"><Lock className="w-6 h-6 text-red-400" />Information Governance</h1>
          <p className="text-sm text-muted-foreground mt-1">GDPR Art 33/34 Breach Management + Art 15 DSAR Handling — DSPT Compliance</p>
        </div>

        {/* DPO Configuration Card */}
        {isAdmin && (
          <Card className={`glass-card ${dpoSettings.dpo_email ? 'border-border' : 'border-amber-500/40'}`} data-testid="dpo-settings-card">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-[#00A6A0]" />
                  <span className="text-sm font-medium text-foreground">Data Protection Officer</span>
                  {dpoSettings.dpo_email ? (
                    <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-[#A8E6CF]/10 text-[#A8E6CF]">Configured</span>
                  ) : (
                    <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-500/10 text-amber-400">Not Set — Breach alerts disabled</span>
                  )}
                </div>
                <Button size="sm" variant="ghost" onClick={() => setDpoEditing(!dpoEditing)} data-testid="edit-dpo-btn">
                  <Settings className="w-4 h-4" />
                </Button>
              </div>
              {!dpoEditing && dpoSettings.dpo_email && (
                <p className="text-xs text-muted-foreground">{dpoSettings.dpo_name} &bull; {dpoSettings.dpo_email}{dpoSettings.ico_registration_number ? ` — ICO: ${dpoSettings.ico_registration_number}` : ''}</p>
              )}
              {!dpoEditing && !dpoSettings.dpo_email && (
                <p className="text-xs text-amber-400">Configure your DPO email to receive automatic breach notifications within the 72-hour ICO window.</p>
              )}
              {dpoEditing && (
                <div className="grid grid-cols-2 gap-2 mt-2">
                  <Input value={dpoSettings.dpo_name} onChange={e => setDpoSettings(s => ({ ...s, dpo_name: e.target.value }))} placeholder="DPO Name" className="bg-muted border-border text-foreground text-sm" data-testid="dpo-name" />
                  <Input value={dpoSettings.dpo_email} onChange={e => setDpoSettings(s => ({ ...s, dpo_email: e.target.value }))} placeholder="DPO Email" className="bg-muted border-border text-foreground text-sm" data-testid="dpo-email" />
                  <Input value={dpoSettings.dpo_phone} onChange={e => setDpoSettings(s => ({ ...s, dpo_phone: e.target.value }))} placeholder="DPO Phone" className="bg-muted border-border text-foreground text-sm" />
                  <Input value={dpoSettings.ico_registration_number} onChange={e => setDpoSettings(s => ({ ...s, ico_registration_number: e.target.value }))} placeholder="ICO Registration Number" className="bg-muted border-border text-foreground text-sm" />
                  <div className="col-span-2 flex gap-2 justify-end">
                    <Button size="sm" variant="outline" onClick={() => setDpoEditing(false)} className="border-border text-xs">Cancel</Button>
                    <Button size="sm" onClick={saveDPO} className="btn-primary text-xs" data-testid="save-dpo-btn">Save DPO Settings</Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        <div className="flex gap-1 p-1 bg-muted rounded-lg">
          <button onClick={() => setTab('breaches')} className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-colors relative ${tab === 'breaches' ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground'}`} data-testid="tab-breaches">
            Breach Register
            {breaches.filter(b => b.ico_overdue).length > 0 && <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-red-500 text-white text-[10px] flex items-center justify-center">{breaches.filter(b => b.ico_overdue).length}</span>}
          </button>
          <button onClick={() => setTab('dsar')} className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-colors relative ${tab === 'dsar' ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground'}`} data-testid="tab-dsar">
            DSAR Register
            {dsars.filter(d => d.overdue).length > 0 && <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-red-500 text-white text-[10px] flex items-center justify-center">{dsars.filter(d => d.overdue).length}</span>}
          </button>
          {isAdmin && (
            <button onClick={() => setTab('inspector')} className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-colors ${tab === 'inspector' ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground'}`} data-testid="tab-inspector">
              Inspector Access
            </button>
          )}
          {isAdmin && (
            <button onClick={() => setTab('erasure')} className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-colors ${tab === 'erasure' ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground'}`} data-testid="tab-erasure" aria-label="Right to Erasure">
              Erasure
            </button>
          )}
          {isAdmin && (
            <button onClick={() => setTab('retention')} className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-colors relative ${tab === 'retention' ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground'}`} data-testid="tab-retention" aria-label="Data Retention">
              Retention
              {retentionFlagged.length > 0 && <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-amber-500 text-white text-[10px] flex items-center justify-center">{retentionFlagged.length}</span>}
            </button>
          )}
        </div>

        {/* ====== BREACH REGISTER ====== */}
        {tab === 'breaches' && (
          <div className="space-y-4" data-testid="breach-section">
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">GDPR Art 33/34 — 72-hour ICO notification requirement</p>
              <Button size="sm" className="bg-red-500 hover:bg-red-600 text-white" onClick={() => setBreachDialog(true)} data-testid="log-breach-btn"><AlertTriangle className="w-4 h-4 mr-1" />Log Breach</Button>
            </div>
            {breaches.length === 0 ? (
              <div className="text-center py-8"><Shield className="w-12 h-12 text-muted-foreground mx-auto mb-3 opacity-30" /><p className="text-muted-foreground">No data breaches recorded</p></div>
            ) : (
              <div className="space-y-3">
                {breaches.map(b => (
                  <Card key={b.id} className={`glass-card border-border ${b.ico_overdue ? 'border-red-500/40' : ''}`} data-testid={`breach-${b.id}`}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${SEV_COLORS[b.severity]}`}>{b.severity?.toUpperCase()}</span>
                            <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${b.status === 'closed' ? 'bg-muted text-muted-foreground' : 'bg-amber-500/10 text-amber-400'}`}>{b.status}</span>
                            {b.ico_overdue && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-red-500/20 text-red-400 font-medium">ICO DEADLINE PASSED</span>}
                            {b.hours_until_ico_deadline != null && !b.ico_overdue && b.status !== 'closed' && (
                              <span className="text-[10px] text-amber-400"><Clock className="w-3 h-3 inline mr-0.5" />{b.hours_until_ico_deadline}h to ICO deadline</span>
                            )}
                          </div>
                          <p className="text-sm text-foreground mt-1">{b.description?.slice(0, 200)}</p>
                          <p className="text-[10px] text-muted-foreground mt-1">Detected: {b.detected_date} &bull; ~{b.approximate_subjects_affected} subjects &bull; Logged by {b.logged_by_name}</p>
                        </div>
                        <div className="flex gap-1">
                          {b.status === 'detected' && <Button size="sm" variant="outline" className="text-xs border-border" onClick={() => updateBreachStatus(b.id, 'contained')}>Contained</Button>}
                          {b.status === 'contained' && <Button size="sm" variant="outline" className="text-xs border-border" onClick={() => updateBreachStatus(b.id, 'closed')}>Close</Button>}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ====== DSAR REGISTER ====== */}
        {tab === 'dsar' && (
          <div className="space-y-4" data-testid="dsar-section">
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">GDPR Art 15 — 1 calendar month response deadline</p>
              <Button size="sm" className="btn-primary" onClick={() => setDsarDialog(true)} data-testid="log-dsar-btn"><Plus className="w-4 h-4 mr-1" />Log DSAR</Button>
            </div>
            {dsars.length === 0 ? (
              <div className="text-center py-8"><FileText className="w-12 h-12 text-muted-foreground mx-auto mb-3 opacity-30" /><p className="text-muted-foreground">No DSARs logged</p></div>
            ) : (
              <div className="space-y-3">
                {dsars.map(d => (
                  <Card key={d.id} className={`glass-card border-border ${d.overdue ? 'border-red-500/40' : ''}`} data-testid={`dsar-${d.id}`}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-sm text-foreground font-medium">{d.requester?.name || 'Unknown'}</span>
                            <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${d.status === 'sent' || d.status === 'closed' ? 'bg-[#A8E6CF]/10 text-[#A8E6CF]' : 'bg-amber-500/10 text-amber-400'}`}>{d.status?.replace(/_/g, ' ')}</span>
                            {d.requester?.identity_verified && <CheckCircle2 className="w-3 h-3 text-[#A8E6CF]" />}
                            {d.overdue && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-red-500/20 text-red-400">OVERDUE</span>}
                            {d.days_until_deadline != null && !d.overdue && d.status !== 'sent' && d.status !== 'closed' && (
                              <span className="text-[10px] text-muted-foreground">{d.days_until_deadline}d to deadline</span>
                            )}
                          </div>
                          <p className="text-[10px] text-muted-foreground mt-1">
                            Subject: {d.subject_name || d.subject_type} &bull; Received: {d.date_received} &bull; Deadline: {d.deadline}
                            {d.extension_applied && ' (extended)'}
                          </p>
                        </div>
                        <div className="flex gap-1 flex-shrink-0">
                          {d.status === 'received' && <Button size="sm" variant="outline" className="text-xs" onClick={() => updateDSARStatus(d.id, 'verifying_identity')}>Verify ID</Button>}
                          {d.status === 'verifying_identity' && <Button size="sm" variant="outline" className="text-xs" onClick={() => updateDSARStatus(d.id, 'searching')}>Search</Button>}
                          {d.status === 'searching' && <Button size="sm" variant="outline" className="text-xs" onClick={() => updateDSARStatus(d.id, 'redacting')}>Redact</Button>}
                          {d.status === 'redacting' && <Button size="sm" variant="outline" className="text-xs" onClick={() => updateDSARStatus(d.id, 'ready')}>Ready</Button>}
                          {d.status === 'ready' && <Button size="sm" className="btn-primary text-xs" onClick={() => updateDSARStatus(d.id, 'sent')}>Mark Sent</Button>}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ====== INSPECTOR ACCESS ====== */}
        {tab === 'inspector' && isAdmin && (
          <div className="space-y-4" data-testid="inspector-section">
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">Generate time-limited read-only links for CQC inspectors</p>
              <Button size="sm" className="btn-primary" onClick={() => { setTokenDialog(true); setNewToken(null); setTokenForm({ inspector_name: '', purpose: 'CQC Inspection', hours: 72 }); }} data-testid="generate-token-btn">
                <Link2 className="w-4 h-4 mr-1" />Generate Inspector Link
              </Button>
            </div>

            {newToken && (
              <Card className="glass-card border-[#00A6A0]/40" data-testid="new-token-card">
                <CardContent className="p-4">
                  <p className="text-xs text-[#00A6A0] font-medium mb-2">New Inspector Link Generated</p>
                  <div className="flex items-center gap-2">
                    <Input value={`${window.location.origin}/inspect/${newToken.token}`} readOnly className="bg-muted border-border text-foreground text-xs font-mono flex-1" />
                    <Button size="sm" variant="outline" className="border-border" onClick={() => copyInspectorUrl(newToken.token)} data-testid="copy-token-btn">
                      <Copy className="w-4 h-4" />
                    </Button>
                    <Button size="sm" variant="outline" className="border-border" onClick={() => window.open(`/inspect/${newToken.token}`, '_blank')} data-testid="open-token-btn">
                      <ExternalLink className="w-4 h-4" />
                    </Button>
                  </div>
                  <p className="text-[10px] text-muted-foreground mt-2">Expires: {new Date(newToken.expires_at).toLocaleString()} ({newToken.hours}h)</p>
                </CardContent>
              </Card>
            )}

            {inspectorTokens.length === 0 ? (
              <div className="text-center py-8"><Shield className="w-12 h-12 text-muted-foreground mx-auto mb-3 opacity-30" /><p className="text-muted-foreground">No inspector tokens generated</p></div>
            ) : (
              <div className="space-y-2">
                {inspectorTokens.map(t => (
                  <Card key={t.id} className={`glass-card border-border ${!t.is_active ? 'opacity-50' : ''}`} data-testid={`token-${t.id}`}>
                    <CardContent className="p-3">
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-sm text-foreground font-medium">{t.inspector_name || 'Inspector'}</span>
                            <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${t.is_active ? 'bg-[#A8E6CF]/10 text-[#A8E6CF]' : t.revoked ? 'bg-red-500/10 text-red-400' : 'bg-muted text-muted-foreground'}`}>
                              {t.revoked ? 'Revoked' : t.is_expired ? 'Expired' : 'Active'}
                            </span>
                          </div>
                          <p className="text-[10px] text-muted-foreground mt-0.5">{t.purpose} &bull; Created {new Date(t.created_at).toLocaleDateString()} &bull; Expires {new Date(t.expires_at).toLocaleString()}</p>
                        </div>
                        <div className="flex gap-1 flex-shrink-0">
                          {t.is_active && (
                            <>
                              <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => copyInspectorUrl(t.token)} title="Copy URL"><Copy className="w-3.5 h-3.5" /></Button>
                              <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => window.open(`/inspect/${t.token}`, '_blank')} title="Open"><ExternalLink className="w-3.5 h-3.5" /></Button>
                              <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-red-400 hover:text-red-500" onClick={() => revokeToken(t.id)} title="Revoke"><Trash2 className="w-3.5 h-3.5" /></Button>
                            </>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ====== RIGHT TO ERASURE (GDPR Art 17) ====== */}
        {tab === 'erasure' && isAdmin && (
          <div className="space-y-4" data-testid="erasure-section">
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">GDPR Art 17 - Right to Erasure / Data Anonymisation</p>
              <Button size="sm" className="bg-red-500 hover:bg-red-600 text-white" onClick={() => setErasureDialog(true)} data-testid="create-erasure-btn">
                <UserX className="w-4 h-4 mr-1" />New Erasure Request
              </Button>
            </div>

            <Card className="glass-card border-amber-500/30">
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-amber-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-foreground">How Data Erasure Works</p>
                    <p className="text-xs text-muted-foreground mt-1">When executed, personal data is replaced with [REDACTED] across all collections (user records, care plans, reports, messages, etc.). Audit trail entries are preserved for legal compliance. This action is <strong className="text-red-400">irreversible</strong>.</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {erasureRequests.length === 0 ? (
              <div className="text-center py-8"><UserX className="w-12 h-12 text-muted-foreground mx-auto mb-3 opacity-30" /><p className="text-muted-foreground">No erasure requests</p></div>
            ) : (
              <div className="space-y-2">
                {erasureRequests.map(r => (
                  <Card key={r.id} className="glass-card border-border" data-testid={`erasure-${r.id}`}>
                    <CardContent className="p-3">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="text-sm text-foreground font-medium">{r.subject_name || 'Unknown'}</span>
                            <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${r.status === 'completed' ? 'bg-[#A8E6CF]/10 text-[#A8E6CF]' : r.status === 'pending_review' ? 'bg-amber-500/10 text-amber-400' : 'bg-muted text-muted-foreground'}`}>
                              {r.status?.replace('_', ' ')}
                            </span>
                          </div>
                          <p className="text-[10px] text-muted-foreground mt-0.5">
                            Requested by {r.requested_by_name} on {new Date(r.created_at).toLocaleDateString()}
                            {r.records_affected ? ` - ${r.records_affected} records anonymised` : ''}
                          </p>
                          {r.reason && <p className="text-xs text-muted-foreground mt-1">Reason: {r.reason}</p>}
                        </div>
                        {r.status === 'pending_review' && (
                          <Button size="sm" className="bg-red-500 hover:bg-red-600 text-white text-xs" data-testid={`execute-erasure-${r.id}`}
                            onClick={async () => {
                              if (!window.confirm('This will permanently anonymise all personal data for this subject. This action cannot be undone. Continue?')) return;
                              try {
                                const { data } = await axios.post(`${API}/api/ig-compliance/erasure-execute/${r.id}`, {}, { withCredentials: true });
                                toast.success(`Erasure complete: ${data.records_affected} records anonymised`);
                                fetchErasure();
                              } catch (err) { toast.error(err.response?.data?.detail || 'Erasure failed'); }
                            }}>
                            <Trash2 className="w-3 h-3 mr-1" />Execute Erasure
                          </Button>
                        )}
                      </div>
                      {r.collections_anonymised?.length > 0 && (
                        <p className="text-[10px] text-muted-foreground mt-1">Collections: {r.collections_anonymised.join(', ')}</p>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ====== RETENTION MANAGEMENT (GDPR Art 5(1)(e)) ====== */}
        {tab === 'retention' && isAdmin && (
          <div className="space-y-4" data-testid="retention-section">
            <p className="text-sm text-muted-foreground">GDPR Art 5(1)(e) - Storage Limitation / NHS Records Management Code</p>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {Object.entries(retentionPolicy).map(([type, years]) => (
                <Card key={type} className="glass-card border-border">
                  <CardContent className="p-3 text-center">
                    <p className="text-lg font-bold text-[#00A6A0]">{years}y</p>
                    <p className="text-xs text-muted-foreground mt-1">{type.replace(/_/g, ' ')}</p>
                  </CardContent>
                </Card>
              ))}
            </div>

            {retentionFlagged.length === 0 ? (
              <Card className="glass-card border-[#A8E6CF]/30">
                <CardContent className="p-4 text-center">
                  <CheckCircle2 className="w-8 h-8 text-[#A8E6CF] mx-auto mb-2" />
                  <p className="text-sm text-foreground">No records exceed their retention period</p>
                  <p className="text-xs text-muted-foreground mt-1">Checked daily at 02:00 UTC</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-2">
                <Card className="glass-card border-amber-500/30">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Archive className="w-4 h-4 text-amber-400" />
                      <span className="text-sm font-medium text-foreground">Records Exceeding Retention Period</span>
                    </div>
                    <p className="text-xs text-muted-foreground mb-3">These records have exceeded their configured retention period. Review and archive as appropriate.</p>
                    <div className="space-y-2">
                      {retentionFlagged.map(f => (
                        <div key={f.record_type} className="flex items-center justify-between bg-muted/50 rounded-lg p-3" data-testid={`retention-${f.record_type}`}>
                          <div>
                            <p className="text-sm text-foreground font-medium">{f.record_type.replace(/_/g, ' ')}</p>
                            <p className="text-[10px] text-muted-foreground">{f.records_count} records &bull; Retention: {f.retention_years}y &bull; Oldest: {f.oldest_record_date}</p>
                          </div>
                          <Button size="sm" variant="outline" className="border-amber-500/40 text-amber-400 text-xs" data-testid={`archive-${f.record_type}`}
                            onClick={async () => {
                              if (!window.confirm(`Archive ${f.records_count} ${f.record_type} records older than ${f.retention_years} years?`)) return;
                              try {
                                const { data } = await axios.post(`${API}/api/ig-compliance/retention-archive`, { record_type: f.record_type }, { withCredentials: true });
                                toast.success(`Archived ${data.records_archived} records`);
                                fetchRetention();
                              } catch (err) { toast.error(err.response?.data?.detail || 'Archive failed'); }
                            }}>
                            <Archive className="w-3 h-3 mr-1" />Archive
                          </Button>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Erasure Request Dialog */}
      <Dialog open={erasureDialog} onOpenChange={setErasureDialog}>
        <DialogContent className="bg-background border-border max-w-md">
          <DialogHeader><DialogTitle className="text-foreground text-red-400">Right to Erasure Request</DialogTitle><DialogDescription>Create a GDPR Art 17 data anonymisation request. The data will be reviewed before execution.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs text-muted-foreground">Select Data Subject (Client)</label>
              <select value={erasureForm.subject_id} onChange={e => setErasureForm(f => ({ ...f, subject_id: e.target.value }))} className="w-full bg-muted border border-border rounded-md px-3 py-2 text-foreground text-sm mt-1" data-testid="erasure-subject">
                <option value="">Select client...</option>
                {clients.map(c => <option key={c.id || c._id} value={c.id || c._id}>{c.name} ({c.email})</option>)}
              </select>
            </div>
            <textarea value={erasureForm.reason} onChange={e => setErasureForm(f => ({ ...f, reason: e.target.value }))} placeholder="Reason for erasure request..." className="w-full bg-muted border border-border rounded-md px-3 py-2 text-foreground text-sm min-h-[60px]" data-testid="erasure-reason" />
            <Button className="w-full bg-red-500 hover:bg-red-600 text-white" data-testid="submit-erasure-btn" disabled={!erasureForm.subject_id}
              onClick={async () => {
                try {
                  await axios.post(`${API}/api/ig-compliance/erasure-request`, erasureForm, { withCredentials: true });
                  toast.success('Erasure request created — pending review');
                  setErasureDialog(false);
                  setErasureForm({ subject_id: '', reason: '' });
                  fetchErasure();
                } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
              }}>
              Create Erasure Request
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      <Dialog open={breachDialog} onOpenChange={setBreachDialog}>
        <DialogContent className="bg-background border-border max-w-md">
          <DialogHeader><DialogTitle className="text-foreground text-red-400">Log Data Breach</DialogTitle><DialogDescription>Record a GDPR Art 33/34 data breach — 72-hour ICO notification timer starts immediately.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <textarea value={breachForm.description} onChange={e => setBreachForm(f => ({ ...f, description: e.target.value }))} placeholder="Describe the breach..." className="w-full bg-muted border border-border rounded-md px-3 py-2 text-foreground text-sm min-h-[80px]" data-testid="breach-description" />
            <div className="grid grid-cols-2 gap-2">
              <select value={breachForm.severity} onChange={e => setBreachForm(f => ({ ...f, severity: e.target.value }))} className="bg-muted border border-border rounded-md px-3 py-2 text-foreground text-sm" data-testid="breach-severity">
                <option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option><option value="critical">Critical</option>
              </select>
              <Input type="number" value={breachForm.subjects_affected} onChange={e => setBreachForm(f => ({ ...f, subjects_affected: parseInt(e.target.value) || 0 }))} placeholder="Subjects affected" className="bg-muted border-border text-foreground" />
            </div>
            <textarea value={breachForm.containment_actions} onChange={e => setBreachForm(f => ({ ...f, containment_actions: e.target.value }))} placeholder="Containment actions taken (one per line)" className="w-full bg-muted border border-border rounded-md px-3 py-2 text-foreground text-sm min-h-[50px]" />
            <Button className="w-full bg-red-500 hover:bg-red-600 text-white" onClick={saveBreach} data-testid="submit-breach-btn">Log Breach (Starts 72hr Timer)</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* DSAR Dialog */}
      <Dialog open={dsarDialog} onOpenChange={setDsarDialog}>
        <DialogContent className="bg-background border-border max-w-md">
          <DialogHeader><DialogTitle className="text-foreground">Log Subject Access Request</DialogTitle><DialogDescription>Record a GDPR Art 15 DSAR — 1-month statutory deadline begins from receipt.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <Input value={dsarForm.requester_name} onChange={e => setDsarForm(f => ({ ...f, requester_name: e.target.value }))} placeholder="Requester name" className="bg-muted border-border text-foreground" data-testid="dsar-name" />
            <Input value={dsarForm.requester_email} onChange={e => setDsarForm(f => ({ ...f, requester_email: e.target.value }))} placeholder="Requester email" className="bg-muted border-border text-foreground" />
            <Input value={dsarForm.subject_name} onChange={e => setDsarForm(f => ({ ...f, subject_name: e.target.value }))} placeholder="Data subject name" className="bg-muted border-border text-foreground" />
            <div className="grid grid-cols-2 gap-2">
              <select value={dsarForm.relationship} onChange={e => setDsarForm(f => ({ ...f, relationship: e.target.value }))} className="bg-muted border border-border rounded-md px-3 py-2 text-foreground text-sm">
                <option value="self">Self</option><option value="legal_representative">Legal Representative</option><option value="parent">Parent/Guardian</option><option value="other">Other</option>
              </select>
              <select value={dsarForm.subject_type} onChange={e => setDsarForm(f => ({ ...f, subject_type: e.target.value }))} className="bg-muted border border-border rounded-md px-3 py-2 text-foreground text-sm">
                <option value="client">Client</option><option value="staff">Staff</option>
              </select>
            </div>
            <Button className="w-full btn-primary" onClick={saveDSAR} data-testid="submit-dsar-btn">Log DSAR (Starts 1-Month Timer)</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Inspector Token Dialog */}
      <Dialog open={tokenDialog} onOpenChange={setTokenDialog}>
        <DialogContent className="bg-background border-border max-w-md">
          <DialogHeader><DialogTitle className="text-foreground">Generate Inspector Access Link</DialogTitle><DialogDescription>Create a time-limited read-only link for CQC inspectors. No login required.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <Input value={tokenForm.inspector_name} onChange={e => setTokenForm(f => ({ ...f, inspector_name: e.target.value }))} placeholder="Inspector name (optional)" className="bg-muted border-border text-foreground" data-testid="token-inspector-name" />
            <Input value={tokenForm.purpose} onChange={e => setTokenForm(f => ({ ...f, purpose: e.target.value }))} placeholder="Purpose" className="bg-muted border-border text-foreground" />
            <div>
              <label className="text-xs text-muted-foreground">Valid for (hours):</label>
              <select value={tokenForm.hours} onChange={e => setTokenForm(f => ({ ...f, hours: parseInt(e.target.value) }))} className="w-full bg-muted border border-border rounded-md px-3 py-2 text-foreground text-sm mt-1">
                <option value={24}>24 hours (1 day)</option>
                <option value={48}>48 hours (2 days)</option>
                <option value={72}>72 hours (3 days)</option>
                <option value={120}>120 hours (5 days)</option>
                <option value={168}>168 hours (7 days)</option>
              </select>
            </div>
            <Button className="w-full btn-primary" onClick={() => { generateToken(); setTokenDialog(false); }} data-testid="submit-token-btn">Generate Secure Link</Button>
          </div>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
